import { useUpdate } from '../contexts/UpdateContext';

const isElectron = typeof window !== 'undefined' && !!window.appUpdater;

export default function UpdateModal() {
  const ctx = useUpdate();
  if (!ctx) return null;

  const { state, version, percent, message, open, setOpen, appVersion } = ctx;

  const close = () => setOpen(false);

  if (!isElectron || !open || !state) return null;

  // Always closeable — download runs in background so user can dismiss at any time
  const canClose = true;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)' }}
      onClick={(e) => { if (canClose && e.target === e.currentTarget) close(); }}
    >
      <div
        className="w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden"
        style={{ background: 'var(--lx-card, #0d1424)', border: '1px solid rgba(99,102,241,0.25)' }}
      >
        {/* Icon area */}
        <div className="flex flex-col items-center pt-8 pb-5 px-6 text-center">
          <StateIcon state={state} percent={percent} />
          <h2 className="text-lg font-bold text-white mt-4 mb-1">
            {TITLES[state]}
          </h2>
          <p className="text-sm text-gray-400 leading-relaxed">
            <StateBody state={state} version={version} percent={percent} message={message} appVersion={appVersion} />
          </p>

          {/* Download progress bar */}
          {state === 'downloading' && (
            <div className="w-full mt-4 h-1.5 rounded-full overflow-hidden"
              style={{ background: 'rgba(99,102,241,0.2)' }}>
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{ width: `${percent}%`, background: 'linear-gradient(90deg,#6366f1,#8b5cf6)' }}
              />
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="px-6 pb-6 flex flex-col gap-2">
          {state === 'downloaded' && (
            <>
              <button
                onClick={() => window.appUpdater.install()}
                className="w-full py-2.5 rounded-xl text-sm font-semibold text-white transition-all"
                style={{ background: 'linear-gradient(135deg,#059669,#10b981)' }}
              >
                Restart &amp; Install
              </button>
              <button onClick={close}
                className="w-full py-2.5 rounded-xl text-sm font-medium text-gray-400 hover:text-gray-200 transition-colors">
                Install on Next Launch
              </button>
            </>
          )}
          {(state === 'downloading' || state === 'available') && (
            <button onClick={close}
              className="w-full py-2.5 rounded-xl text-sm font-medium text-gray-400 hover:text-gray-200 transition-colors">
              Continue in background
            </button>
          )}
          {(state === 'not-available' || state === 'error' || state === 'checking') && canClose && (
            <button
              onClick={close}
              className="w-full py-2.5 rounded-xl text-sm font-semibold transition-all"
              style={{ background: 'rgba(99,102,241,0.15)', color: '#a5b4fc', border: '1px solid rgba(99,102,241,0.3)' }}
            >
              {state === 'checking' ? 'Cancel' : 'Close'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const TITLES = {
  checking:        'Checking for Updates',
  'not-available': "You're Up to Date",
  available:       'Downloading Update…',
  downloading:     'Downloading Update…',
  downloaded:      'Ready to Install',
  error:           'Update Check Failed',
};

function StateIcon({ state, percent }) {
  if (state === 'checking') {
    return (
      <div className="w-16 h-16 rounded-full flex items-center justify-center"
        style={{ background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)' }}>
        <div className="w-7 h-7 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (state === 'downloading') {
    return (
      <div className="relative w-16 h-16">
        <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
          <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(99,102,241,0.15)" strokeWidth="4" />
          <circle cx="32" cy="32" r="28" fill="none" stroke="#6366f1" strokeWidth="4"
            strokeDasharray={`${2 * Math.PI * 28}`}
            strokeDashoffset={`${2 * Math.PI * 28 * (1 - percent / 100)}`}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.3s ease' }}
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-indigo-300">
          {percent}%
        </span>
      </div>
    );
  }
  const icons = {
    'not-available': { bg: 'rgba(16,185,129,0.15)', border: 'rgba(16,185,129,0.3)', icon: '✓', color: '#6ee7b7' },
    available:       { bg: 'rgba(99,102,241,0.15)', border: 'rgba(99,102,241,0.3)', icon: '⬆', color: '#a5b4fc' },
    downloaded:      { bg: 'rgba(16,185,129,0.15)', border: 'rgba(16,185,129,0.3)', icon: '✓', color: '#6ee7b7' },
    error:           { bg: 'rgba(239,68,68,0.15)',  border: 'rgba(239,68,68,0.3)',  icon: '⚠', color: '#fca5a5' },
  };
  const cfg = icons[state] || icons.error;
  return (
    <div className="w-16 h-16 rounded-full flex items-center justify-center text-2xl"
      style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, color: cfg.color }}>
      {cfg.icon}
    </div>
  );
}

function StateBody({ state, version, percent, message, appVersion }) {
  if (state === 'checking')      return 'Looking for the latest version…';
  if (state === 'not-available') return appVersion
    ? <>LearnXops <strong className="text-white">v{appVersion}</strong> is the latest version.</>
    : 'LearnXops is running the latest version.';
  if (state === 'available')     return <>Version <strong className="text-white">{version}</strong> is downloading in the background…</>;
  if (state === 'downloading')   return `Downloading… ${percent}% complete`;
  if (state === 'downloaded')    return <>Version <strong className="text-white">{version}</strong> has been downloaded and is ready to install.</>;
  if (state === 'error')         return message;
  return null;
}
